'use client'

import React, { useState, useEffect } from 'react'
import { HeaderSection } from '@/components/shared/header-section'
import { PageContent } from '@/components/shared/page-content'
import { useLazyQuery } from '@apollo/client'
import { DataTableManual } from '@/components/shared/data-table-manual/data-table-manual'
import { DataTableToolbar } from '@/components/shared/data-table-manual/data-table-toolbar-manual'
import { Button } from '@/components/ui/button'
import { Sheet } from '@/components/ui/sheet'
import { Plus } from 'lucide-react'
import { createAllCustomerAccountsColumns } from './components/allCustomerAccountsColumns'
import { CustomerAccount } from './types'
import CustomerAccountForm from './CustomerAccountForm'
import { useDeleteConfirmation } from '@/hooks/useDeleteConfirmation'
import {
  FIND_ALL_CUSTOMER_ACCOUNTS,
  DELETE_MANY_CUSTOMER_ACCOUNTS,
} from './customerAccountQueries'

export default function CustomerAccounts() {
  const [searchTerm, setSearchTerm] = useState('')
  const [tableData, setTableData] = useState<CustomerAccount[]>([])
  const [tableDataCount, setTableDataCount] = useState(0)
  const [order, setOrder] = useState([])
  const [filters, setFilters] = React.useState<Map<string, Set<string>>>(
    new Map()
  )
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingAccount, setEditingAccount] = useState<CustomerAccount | null>(
    null
  )

  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 20,
  })

  const [loadCustomerAccounts, { data, error, loading }] = useLazyQuery(
    FIND_ALL_CUSTOMER_ACCOUNTS,
    {
      variables: {},
      fetchPolicy: 'cache-and-network',
    }
  )

  const [loadAllCustomerAccounts] = useLazyQuery(FIND_ALL_CUSTOMER_ACCOUNTS, {
    fetchPolicy: 'cache-and-network',
  })

  const { onDelete } = useDeleteConfirmation({
    mutation: DELETE_MANY_CUSTOMER_ACCOUNTS,
    refetchQueries: ['FIND_ALL_CUSTOMER_ACCOUNTS'],
    messages: {
      success: 'Customer accounts deleted successfully',
      error: 'Failed to delete customer accounts',
    },
    itemName: 'customer account',
  })

  const handleExportCsv = React.useCallback(async () => {
    const queryFilter = Array.from(filters.entries())
      .map(([key, value]) => {
        if (!value.size) return null
        return {
          [key]: {
            in: Array.from(value),
          },
        }
      })
      .filter(Boolean)

    const { data: exportData } = await loadAllCustomerAccounts({
      variables: {
        searchTerm,
        take: 100000, // Get all records (using large number instead of -1)
        skip: 0,
        filters: queryFilter,
      },
    })

    if (exportData) {
      return exportData.findManyCustomerAccount || []
    }

    return []
  }, [filters, searchTerm, loadAllCustomerAccounts])

  // Load data when component mounts or when filters/pagination change
  useEffect(() => {
    const queryFilter = Array.from(filters.entries())
      .map(([key, value]) => {
        if (!value.size) return null
        return {
          [key]: {
            in: Array.from(value),
          },
        }
      })
      .filter(Boolean)

    loadCustomerAccounts({
      variables: {
        searchTerm,
        take: pagination.pageSize,
        skip: pagination.pageIndex * pagination.pageSize,
        filters: queryFilter,
        // orderBy: getOrderSortParams(order), // You might need to implement this utility
      },
    })
  }, [searchTerm, pagination, order, filters, loadCustomerAccounts])

  // Reset pagination when search term or filters change
  useEffect(() => {
    setPagination({ ...pagination, pageIndex: 0 })
  }, [searchTerm, pagination.pageSize, order])

  // Update table data when query result changes
  useEffect(() => {
    if (data) {
      setTableData(data.findManyCustomerAccount || [])
      setTableDataCount(data.findManyCustomerAccountCount || 0)
    }
  }, [data])

  const handleCreateNew = () => {
    setEditingAccount(null)
    setIsFormOpen(true)
  }

  const handleEdit = (account: CustomerAccount) => {
    setEditingAccount(account)
    setIsFormOpen(true)
  }

  const handleCloseForm = () => {
    setIsFormOpen(false)
    setEditingAccount(null)
  }

  const columns = createAllCustomerAccountsColumns(handleEdit)

  const heading = 'Customer Accounts'
  const text = 'Manage all customer accounts across all customers'

  return (
    <PageContent>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <HeaderSection title={heading} subtitle={text} />
        {/* <Button onClick={handleCreateNew}>
          <Plus className="h-4 w-4 mr-2" />
          Create New
        </Button> */}
      </div>

      <DataTableManual
        data={tableData}
        columns={columns}
        rowCount={tableDataCount}
        pageIndex={pagination.pageIndex}
        pageSize={pagination.pageSize}
        onPaginationChange={setPagination}
        onSortingChange={setOrder}
        onColumnFiltersChange={setFilters}
        isLoading={loading}
        onExportCsv={handleExportCsv}
        exportFileName="customer-accounts.csv"
        toolbar={(table: any) => (
          <DataTableToolbar
            table={table}
            config={{
              onDelete: onDelete,
              search: {
                placeholder: 'Filter customer accounts...',
                accessor: 'email',
                onSearch: setSearchTerm,
                onFilter: setFilters,
              },
              columns: [
                // Add filter columns here if needed
                // For example, filter by customer, location, etc.
              ],
              initialFilters: filters,
            }}
          />
        )}
      />

      <Sheet open={isFormOpen} onOpenChange={setIsFormOpen}>
        <CustomerAccountForm
          data={{
            id: editingAccount?.id,
            customerId: editingAccount?.customerId || 0,
          }}
          onRequestClose={handleCloseForm}
        />
      </Sheet>
    </PageContent>
  )
}
